import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const UtilityExpenseFormPage = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();

    const [expenseTypes, setExpenseTypes] = useState([]);
    const [banks, setBanks] = useState([]);

    const [pageLoading, setPageLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [expenseTypeId, setExpenseTypeId] = useState("");
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [note, setNote] = useState("");
    const [bankId, setBankId] = useState("");
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
    const [paymentNotes, setPaymentNotes] = useState("");

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [typesRes, banksRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/admin/expense-types?type=Utility Expense`, {
                        headers: getAuthHeaders(),
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/admin/banks`, {
                        headers: getAuthHeaders(),
                    }).catch(() => ({ data: [] })),
                ]);

                const loadedBanks = banksRes.data?.data || banksRes.data || [];
                setExpenseTypes(typesRes.data?.data || typesRes.data || []);
                setBanks(loadedBanks);

                if (isEditMode) {
                    const res = await axios.get(
                        `${process.env.REACT_APP_API_URL}/admin/utility-expenses/${id}`,
                        { headers: getAuthHeaders() }
                    );
                    const item = res.data?.data || res.data;

                    setExpenseTypeId(item.expense_type?.id || "");
                    setAmount(item.amount || "");
                    setDate(item.date || new Date().toISOString().split("T")[0]);
                    setNote(item.note || "");
                    setBankId(item.payment?.bank?.id || (loadedBanks[0]?.id || ""));
                    setPaymentDate(item.payment?.payment_date || item.date || new Date().toISOString().split("T")[0]);
                    setPaymentNotes(item.payment?.notes || "");
                } else if (loadedBanks.length > 0) {
                    setBankId(loadedBanks[0].id);
                }
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load form details.");
            } finally {
                setPageLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setError("");

        const payload = {
            expense_type_id: expenseTypeId,
            amount: parseFloat(amount) || 0,
            date,
            note,
            bank_id: bankId,
            payment_date: paymentDate,
            payment_notes: paymentNotes || null,
        };

        try {
            if (isEditMode) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/admin/utility-expenses/${id}`,
                    payload,
                    { headers: getAuthHeaders() }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/admin/utility-expenses`,
                    payload,
                    { headers: getAuthHeaders() }
                );
            }
            navigate("/utility-expenses");
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <Container fluid className="py-5 text-center">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2 text-muted">Loading utility expense data...</div>
                </Container>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">
                            {isEditMode ? "Edit Utility Expense" : "Record Utility Expense"}
                        </h2>
                        <p className="text-muted mb-0">
                            {isEditMode ? "Update bill payment record." : "Create new utility payment entry."}
                        </p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="secondary" onClick={() => navigate("/utility-expenses")}>
                            Back to List
                        </Button>
                    </Col>
                </Row>

                {error && <Alert variant="danger">{error}</Alert>}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                        <Form onSubmit={handleSubmit}>
                            <h6 className="fw-bold text-muted small text-uppercase mb-3">Expense Details</h6>
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Expense Type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={expenseTypeId}
                                        onChange={(e) => {
                                            setExpenseTypeId(e.target.value);
                                            setErrors((prev) => ({ ...prev, expense_type_id: "" }));
                                        }}
                                        isInvalid={!!errors.expense_type_id}
                                        className="bg-light border-0 py-2"
                                    >
                                        <option value="">Select Expense Type</option>
                                        {expenseTypes.map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.expense_type_id) ? errors.expense_type_id[0] : errors.expense_type_id}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Amount <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={amount}
                                        onChange={(e) => {
                                            setAmount(e.target.value);
                                            setErrors((prev) => ({ ...prev, amount: "" }));
                                        }}
                                        isInvalid={!!errors.amount}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.amount) ? errors.amount[0] : errors.amount}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Expense Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={date}
                                        onChange={(e) => {
                                            setDate(e.target.value);
                                            setErrors((prev) => ({ ...prev, date: "" }));
                                        }}
                                        isInvalid={!!errors.date}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.date) ? errors.date[0] : errors.date}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Note <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Description or reference info"
                                        value={note}
                                        onChange={(e) => {
                                            setNote(e.target.value);
                                            setErrors((prev) => ({ ...prev, note: "" }));
                                        }}
                                        isInvalid={!!errors.note}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.note) ? errors.note[0] : errors.note}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>

                            <hr className="my-4" />

                            <h6 className="fw-bold text-muted small text-uppercase mb-3">Payment Details</h6>
                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Bank / Account <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={bankId}
                                        onChange={(e) => {
                                            setBankId(e.target.value);
                                            setErrors((prev) => ({ ...prev, bank_id: "" }));
                                        }}
                                        isInvalid={!!errors.bank_id}
                                        className="bg-light border-0 py-2"
                                    >
                                        <option value="">Select Bank / Account</option>
                                        {banks.map((b) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name || b.bank_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.bank_id) ? errors.bank_id[0] : errors.bank_id}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Payment Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paymentDate}
                                        onChange={(e) => {
                                            setPaymentDate(e.target.value);
                                            setErrors((prev) => ({ ...prev, payment_date: "" }));
                                        }}
                                        isInvalid={!!errors.payment_date}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.payment_date) ? errors.payment_date[0] : errors.payment_date}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={12}>
                                    <Form.Label className="small text-muted">Payment Notes</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Check #, transaction ref, etc."
                                        value={paymentNotes}
                                        onChange={(e) => setPaymentNotes(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    className="px-4 py-2 fw-semibold"
                                    onClick={() => navigate("/utility-expenses")}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 fw-semibold text-uppercase"
                                >
                                    {saving ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Saving...
                                        </>
                                    ) : isEditMode ? (
                                        "Update Record"
                                    ) : (
                                        "Save Record"
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default UtilityExpenseFormPage;